// Users management — T107
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getUsers, updateUser, deleteUser, type User } from '../services/api';

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getUsers()
      .then((res) => setUsers(res.data || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleRoleChange = async (userId: string, newRole: 'Admin' | 'User') => {
    if (userId === currentUser?.userId) {
      if (!confirm('Are you sure you want to change your own role? You may lose admin access.')) return;
    }
    try {
      const updated = await updateUser(userId, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (userId: string) => {
    if (userId === currentUser?.userId) {
      setError('Cannot delete your own account');
      return;
    }
    if (!confirm('Delete this user?')) return;
    try {
      await deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <div className="animate-pulse bg-surface rounded-lg h-48" />;

  return (
    <div className="space-y-4">
      <h1 className="text-white text-h1">Users</h1>

      {error && <div className="bg-status-down/20 text-status-down rounded px-4 py-3 text-sm">{error}</div>}

      <div className="bg-surface rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-text-muted text-left">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Auth</th>
              <th className="px-4 py-3">Last Login</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const adminCount = users.filter((usr) => usr.role === 'Admin').length;
              const isLastAdmin = u.role === 'Admin' && adminCount <= 1;
              return (
              <tr key={u.id} className="border-b border-border last:border-0 hover:bg-bg-primary/50">
                <td className="px-4 py-3 text-text-primary">{u.email}</td>
                <td className="px-4 py-3">
                  <select
                    aria-label="User role"
                    value={u.role}
                    onChange={(e) => handleRoleChange(u.id, e.target.value as 'Admin' | 'User')}
                    disabled={isLastAdmin}
                    title={isLastAdmin ? 'Cannot change role of last admin' : ''}
                    className="bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm min-h-[44px] disabled:opacity-50"
                  >
                    <option value="Admin">Admin</option>
                    <option value="User">User</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-text-muted">{u.authMethod}</td>
                <td className="px-4 py-3 text-text-muted text-xs">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.id !== currentUser?.userId && !isLastAdmin && (
                    <button
                      onClick={() => handleDelete(u.id)}
                      className="text-status-down hover:text-status-down/80 text-sm min-h-[44px] px-2"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
